import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { fetchAdminArticles } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";

interface RecordingOption {
  slug: string;
  title: string;
  is_published: boolean;
}

/**
 * Pick the article that holds the recording: webinar recordings in the event's own
 * language, by title. The value is the slug — what the API stores — and a slug that
 * is no longer in the list (the recording was re-slugged, or sits in the other
 * language) is kept visible as itself rather than silently dropped.
 */
export function RecordingPicker({
  locale,
  value,
  onChange,
  disabled,
}: {
  locale: string;
  value: string | null;
  onChange: (slug: string | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: queryKeys.recordingOptions(locale),
    queryFn: ({ signal }) =>
      fetchAdminArticles({
        locale,
        format: "zaznam-webinare",
        sortBy: "published_at",
        sortOrder: "desc",
        limit: 50,
        signal,
      }),
  });

  const options: RecordingOption[] =
    data?.items.map((item) => ({
      slug: item.slug,
      title: item.title,
      is_published: item.is_published,
    })) ?? [];
  const selected =
    options.find((option) => option.slug === value) ??
    (value ? { slug: value, title: value, is_published: true } : null);

  return (
    <Autocomplete
      options={
        selected && !options.some((o) => o.slug === selected.slug)
          ? [selected, ...options]
          : options
      }
      value={selected}
      loading={isPending}
      disabled={disabled}
      onChange={(_event, option) => onChange(option?.slug ?? null)}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, current) => option.slug === current.slug}
      noOptionsText={t("eventEditor.fields.recordingNone")}
      data-testid="event.recording"
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("eventEditor.fields.recordingSlug")}
          helperText={t("eventEditor.fields.recordingSlugHelper")}
          placeholder={t("eventEditor.fields.recordingSearch")}
        />
      )}
    />
  );
}
